import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  PlugZap, Loader2, RefreshCw, ScrollText, CheckCircle2, XCircle, Link2, Unplug, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import IsnUpdateLogDialog from "@/components/settings/IsnUpdateLogDialog";

function fmtWhen(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * Settings → ISN Integration. Every user connects their OWN ISN account;
 * admins can view/edit any teammate's connection via the picker. Secrets
 * are stored server-side and never displayed back once saved.
 */
export default function IsnIntegrationCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [targetId, setTargetId] = useState(user?.id || "");
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ service_domain: "", company_key: "", access_key: "", secret_access_key: "" });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const isSelf = !targetId || targetId === user?.id;
  const bodyUser = isSelf ? {} : { user_id: targetId };
  const targetUser = isAdmin ? users.find((u) => u.id === targetId) : null;

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/isn/users")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (user?.id && !targetId) setTargetId(user.id);
  }, [user?.id, targetId]);

  const loadStatus = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/isn/status", { params: isSelf ? {} : { user_id: targetId } });
      setStatus(data);
      setForm({
        service_domain: data.service_domain || "",
        company_key: data.company_key || "",
        access_key: "",
        secret_access_key: "",
      });
    } catch (e) {
      toast.error("Couldn't load ISN status", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [targetId, isSelf]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/isn/test", { ...form, ...bodyUser });
      toast.success("ISN connection OK", { description: data.base_url });
    } catch (e) {
      toast.error("Connection failed", { description: formatApiError(e) });
    } finally {
      setTesting(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/isn/connect", { ...form, ...bodyUser });
      setStatus(data);
      setForm((f) => ({ ...f, access_key: "", secret_access_key: "" }));
      toast.success(status?.connected ? "ISN connection updated" : "ISN connected", {
        description: data.connect_note || data.webhook_note || "Sync is on. Paid inspections will flow in automatically.",
      });
      if (isAdmin) {
        api.get("/isn/users").then(({ data: d }) => setUsers(d.users || [])).catch(() => {});
      }
    } catch (e) {
      toast.error("Couldn't connect", { description: formatApiError(e) });
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/isn/disconnect", { params: isSelf ? {} : { user_id: targetId } });
      toast.success("ISN disconnected");
      await loadStatus();
    } catch (e) {
      toast.error("Couldn't disconnect", { description: formatApiError(e) });
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    const before = status?.last_sync_at || "";
    try {
      await api.post("/isn/sync", { ...bodyUser });
      toast.message("Sync started…", { description: "Pulling the latest paid inspections + call logs." });
      // Poll status until last_sync_at advances (max ~40s).
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        try {
          const { data } = await api.get("/isn/status", { params: isSelf ? {} : { user_id: targetId } });
          if ((data.last_sync_at || "") !== before || tries >= 20) {
            clearInterval(iv);
            setStatus(data);
            setSyncing(false);
            if ((data.last_sync_at || "") !== before) {
              const c = data.counts || {};
              const bits = [];
              if (c.referrals) bits.push(`${c.referrals} referral${c.referrals === 1 ? "" : "s"}`);
              if (c.touches) bits.push(`${c.touches} touch${c.touches === 1 ? "" : "es"}`);
              if (c.enriched) bits.push(`${c.enriched} enriched`);
              if (c.realtors_created) bits.push(`${c.realtors_created} to review`);
              toast.success("Sync complete", {
                description: bits.length ? bits.join(" · ") : "No new activity since last sync.",
              });
            }
          }
        } catch { clearInterval(iv); setSyncing(false); }
      }, 2000);
    } catch (e) {
      setSyncing(false);
      toast.error("Sync failed", { description: formatApiError(e) });
    }
  };

  const connected = !!status?.connected;
  const counts = status?.counts || {};

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="isn-integration-section">
      <div className="flex items-start gap-3 max-w-2xl">
        <div className="rounded-md bg-teal-50 p-2 shrink-0">
          <PlugZap className="h-4 w-4 text-teal-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold text-slate-900 font-display">ISN Integration</h2>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Connect your Inspection Support Network account. Paid inspections flow in as referrals,
            call &amp; text activity lands on realtor timelines, and referring agents are matched to
            your realtor database automatically. Your keys are stored securely and never shown again
            once saved.
          </p>
        </div>
      </div>

      {isAdmin ? (
        <div className="mt-4 max-w-md">
          <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <Users className="h-3 w-3" /> Managing connection for
          </Label>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger className="mt-1 min-h-[40px]" data-testid="isn-user-picker">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {(u.name || u.email)}{u.id === user?.id ? " (you)" : ""} {u.connected ? "· connected" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {/* Status strip */}
      <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="isn-status-strip">
        <span className="inline-flex items-center gap-1.5 text-sm">
          {connected ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : (
            <XCircle className="h-4 w-4 text-slate-400" />
          )}
          <span className={connected ? "text-emerald-700 font-medium" : "text-slate-500"} data-testid="isn-connected-label">
            {loading ? "Loading…" : connected ? "Connected" : "Not connected"}
          </span>
        </span>
        {connected ? (
          <>
            <span className="text-xs text-slate-500">
              Last sync: <span className="text-slate-700" data-testid="isn-last-sync">{fmtWhen(status.last_sync_at)}</span>
              {status.last_sync_status === "error" ? <span className="text-rose-600"> · error</span> : null}
            </span>
            <span className="text-xs text-slate-500">
              Webhook: <span className={status.webhook_registered ? "text-emerald-700" : "text-amber-700"}>
                {status.webhook_registered ? "registered" : "polling only"}
              </span>
            </span>
            {(counts.referrals || counts.touches || counts.realtors_created) ? (
              <span className="text-xs text-slate-500" data-testid="isn-last-counts">
                Last: {counts.referrals || 0} ref · {counts.touches || 0} touch · {counts.realtors_created || 0} to review
              </span>
            ) : null}
          </>
        ) : null}
      </div>

      {/* Credential form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 max-w-2xl">
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">Service domain</Label>
          <Input value={form.service_domain} onChange={set("service_domain")} placeholder="e.g. inspectionsupport.net" className="mt-1 min-h-[40px]" data-testid="isn-service-domain" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">Company key</Label>
          <Input value={form.company_key} onChange={set("company_key")} placeholder="your company key" className="mt-1 min-h-[40px]" data-testid="isn-company-key" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Access key {connected && status?.access_key_hint ? <span className="text-slate-400 normal-case">· saved {status.access_key_hint}</span> : null}
          </Label>
          <Input value={form.access_key} onChange={set("access_key")} placeholder={connected ? "leave blank to keep saved key" : "access key"} className="mt-1 min-h-[40px]" data-testid="isn-access-key" autoComplete="off" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Secret access key {connected && status?.has_secret ? <span className="text-slate-400 normal-case">· saved</span> : null}
          </Label>
          <PasswordInput value={form.secret_access_key} onChange={set("secret_access_key")} placeholder={connected ? "leave blank to keep saved secret" : "secret access key"} className="mt-1 min-h-[40px]" data-testid="isn-secret-key" autoComplete="new-password" />
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-2 max-w-2xl">
        Find these in ISN under <span className="font-medium text-slate-500">Settings → My Access Keys</span>.
        You can regenerate them anytime in ISN and paste the new pair here — nothing else breaks.
      </p>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={testConnection} disabled={testing || connecting} className="min-h-[40px]" data-testid="isn-test-button">
          {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Link2 className="h-4 w-4 mr-1.5" />}
          Test connection
        </Button>
        <Button size="sm" onClick={connect} disabled={connecting} className="min-h-[40px] text-white" style={{ background: "hsl(var(--brand))" }} data-testid="isn-connect-button">
          {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PlugZap className="h-4 w-4 mr-1.5" />}
          {connected ? "Save & reconnect" : "Test & save"}
        </Button>
        {connected ? (
          <>
            <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing} className="min-h-[40px]" data-testid="isn-sync-now-button">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Sync now
            </Button>
            <Button variant="outline" size="sm" onClick={() => setLogOpen(true)} className="min-h-[40px]" data-testid="isn-view-log-button">
              <ScrollText className="h-4 w-4 mr-1.5" /> View update log
            </Button>
            <Button variant="ghost" size="sm" onClick={disconnect} className="min-h-[40px] text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="isn-disconnect-button">
              <Unplug className="h-4 w-4 mr-1.5" /> Disconnect
            </Button>
          </>
        ) : null}
      </div>

      <IsnUpdateLogDialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        userId={isSelf ? null : targetId}
        targetName={targetUser ? (targetUser.name || targetUser.email) : "you"}
      />
    </section>
  );
}
